import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Megaphone, X } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * MandatoryCommPopup — Platform Communications (Doctrine D).
 * Full-screen blocking popup for MANDATORY platform communications, mirroring
 * the lead-notification experience so they cannot be overlooked. Polls
 * /api/platform-comms/pending for the signed-in provider and shows the most
 * recent unacked mandatory comm until acknowledged. Mounted globally.
 */
export const MandatoryCommPopup = () => {
  const [pending, setPending] = useState([]);
  const [acking, setAcking] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    // Only poll when a provider is signed in.
    const token =
      localStorage.getItem('access_token') ||
      localStorage.getItem('auth_token') ||
      localStorage.getItem('token');
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/api/platform-comms/pending`);
      setPending(Array.isArray(data?.pending) ? data.pending : []);
    } catch (e) {
      // silent — endpoint requires provider auth; ignore for non-providers
    }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 60000);
    return () => clearInterval(timer.current);
  }, [load]);

  const current = pending[0];

  const acknowledge = async () => {
    if (!current) return;
    setAcking(true);
    try {
      await axios.post(`${API}/api/platform-comms/ack/${current.id}`);
      setPending((p) => p.filter((n) => n.id !== current.id));
    } catch (e) {
      // keep showing; next poll retries
    } finally {
      setAcking(false);
    }
  };

  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-[6500] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="mandatory-comm-overlay"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
        data-testid="mandatory-comm-popup"
      >
        <div className="bg-violet-600 px-5 py-4 flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
            <Megaphone className="h-5 w-5 text-white" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-100">
              Platform Announcement
            </p>
            <h3 className="text-base font-bold text-white truncate" data-testid="mandatory-comm-title">
              {current.title}
            </h3>
          </div>
        </div>

        <div className="px-5 py-5">
          <div
            className="text-sm leading-relaxed text-slate-700 dark:text-slate-200 whitespace-pre-wrap"
            data-testid="mandatory-comm-body"
          >
            {current.body}
          </div>
          {pending.length > 1 && (
            <p className="mt-3 text-xs text-slate-400">
              +{pending.length - 1} more announcement{pending.length - 1 > 1 ? 's' : ''} after this
            </p>
          )}
        </div>

        <div className="px-5 pb-5 flex items-center gap-2">
          {current.cta_route && current.cta_label && (
            <Button
              variant="outline"
              className="flex-1"
              data-testid="mandatory-comm-cta"
              onClick={() => {
                acknowledge();
                window.location.assign(current.cta_route);
              }}
            >
              {current.cta_label}
            </Button>
          )}
          <Button
            className="flex-1 bg-violet-600 hover:bg-violet-700"
            disabled={acking}
            data-testid="mandatory-comm-ack"
            onClick={acknowledge}
          >
            {acking ? 'Saving…' : 'Got it'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MandatoryCommPopup;
